"use client"

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import SignatureService from '@/api/signature.service';
import { ArrowLeft, Loader2, Edit, Star, Trash2, Mail, PenTool, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { format } from 'date-fns';

export default function SignatureDetail() {
  const [signature, setSignature] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchSignature = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const response = await SignatureService.getSignature(id);
        console.log("getSignature response.data", response.data);
        if (response.data.result === 'success') {
          setSignature(response.data.userSignature);
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to load signature",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching signature:", error);
        toast({
          title: "Error",
          description: "Failed to load signature details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSignature();
  }, [id, toast]);
  
  const handleSetDefault = async () => {
    if (!signature) return;
    
    try {
      const response = await SignatureService.setDefaultSignature(signature.publicId);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Default signature updated successfully",
        });
        
        // Update local state
        setSignature((prev: any) => ({
          ...prev,
          isDefault: true
        }));
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to update default signature",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error setting default signature:", error);
      toast({
        title: "Error",
        description: "An error occurred while updating default signature",
        variant: "destructive",
      });
    }
  };
  
  const handleDelete = async () => {
    if (!signature) return;
    
    try {
      const response = await SignatureService.deleteSignature(signature.publicId);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Signature deleted successfully",
        });
        navigate('/signatures');
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete signature",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting signature:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting the signature",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };
  
  const handleEdit = () => {
    navigate(`/signatures/edit/${signature.publicId}`);
  };
  
  const getSignatureTypeIcon = (type: string) => {
    if (!type) return <Mail className="w-5 h-5 text-gray-500" />;
    
    switch (type.toLowerCase()) {
      case 'text':
        return <PenTool className="w-5 h-5 text-blue-500" />;
      case 'image':
        return <ImageIcon className="w-5 h-5 text-green-500" />;
      case 'both':
        return <Mail className="w-5 h-5 text-purple-500" />;
      default:
        return <Mail className="w-5 h-5 text-gray-500" />;
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading signature details...</p>
      </div>
    );
  }
  
  if (!signature) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <p className="text-muted-foreground">Signature not found</p>
        <Button onClick={() => navigate('/signatures')} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }
  
  return (
    <div className="container py-8 mx-auto max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate('/signatures')} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">
              {signature.name}
            </h1>
            {signature.isDefault && (
              <Badge className="bg-amber-500 hover:bg-amber-600">Default</Badge>
            )}
          </div>
          
          <div className="flex gap-2">
            {!signature.isDefault && (
              <Button 
                variant="outline"
                onClick={handleSetDefault}
                className="gap-2"
              >
                <Star className="w-4 h-4" />
                Set as Default
              </Button>
            )}
            <Button 
              variant="outline"
              onClick={handleEdit}
              className="gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit
            </Button>
            <Button 
              variant="destructive"
              onClick={() => setIsDeleteDialogOpen(true)}
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <Card className="md:col-span-1 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Signature Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  {getSignatureTypeIcon(signature.signatureType)}
                  <div>
                    <p className="text-sm font-medium">Signature Type</p>
                    <p className="text-sm text-muted-foreground">
                      {signature.signatureType === 'text' ? 'Text Only' : 
                       signature.signatureType === 'image' ? 'Image Only' : 
                       signature.signatureType === 'both' ? 'Text & Image' : 'Unknown'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Default Status</p>
                    <p className="text-sm text-muted-foreground">
                      {signature.isDefault ? 'Default Signature' : 'Standard Signature'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Contact Info</p>
                    <p className="text-sm text-muted-foreground">
                      {signature.includeContactInfo ? 'Includes business contact info' : 'No contact info'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Disclaimer</p>
                    <p className="text-sm text-muted-foreground">
                      {signature.includeDisclaimer ? 'Includes disclaimer' : 'No disclaimer'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Created On</p>
                    <p className="text-sm text-muted-foreground">
                      {signature.dateCreated ? format(new Date(signature.dateCreated), "MMM d, yyyy") : "Unknown"}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Right Column - Preview */}
          <Card className="md:col-span-2 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Signature Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-6 bg-white border rounded-md">
                {/* Image Preview */}
                {(signature.signatureType === 'image' || signature.signatureType === 'both') && signature.imageLink && (
                  <div className="mb-4">
                    <img 
                      src={signature.imageLink}
                      alt="Signature"
                      className="max-w-full max-h-48 object-contain"
                    />
                  </div>
                )}
                
                {/* HTML Content */}
                {(signature.signatureType === 'text' || signature.signatureType === 'both') && signature.content && (
                  <div 
                    className="prose max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: signature.content }}
                  />
                )}
                
                {/* Disclaimer */}
                {signature.includeDisclaimer && signature.disclaimer && (
                  <>
                    <Separator className="my-4" />
                    <div className="text-xs text-muted-foreground">
                      {signature.disclaimer}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the signature.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 